import { differenceInDays, format } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	Button,
	Checkbox,
	DatePicker,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	InformationCircleOutline,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Radio,
	RadioGroup,
	Stack,
	Text,
	Tooltip,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useActiveReviewCycle } from '../../../../modules/admin-growth/hooks/use-review-cycles';
import { formatReviewCadence, getLastCompletedPeriod } from '../../../../modules/admin-growth/types/review-cycle';
import { useCreateReviewRecord } from '../hooks/use-review-records';

interface CreateReviewFormData {
	title?: string;
	timeframeType: 'current-quarter' | 'custom';
	timePeriodStart?: Date;
	timePeriodEnd?: Date;
	isPublic: boolean;
	visibility: 'public' | 'private';
}

interface CreateReviewModalProps {
	isOpen: boolean;
	onClose: () => void;
	userId: string;
}

export const CreateReviewModal: FC<CreateReviewModalProps> = ({ isOpen, onClose, userId }) => {
	const createReviewRecord = useCreateReviewRecord();
	const toast = useToast();
	const currentUser = useCurrentUser();
	const { data: targetUser } = useUser(userId);
	const { data: activeReviewCycle } = useActiveReviewCycle();

	// Track whether the user has manually edited the title
	const [isTitleManuallyEdited, setIsTitleManuallyEdited] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// Determine if this is a manager creating a review for someone else
	const isManagerCreatingForOther = userId !== currentUser.id;
	const targetDisplayName = targetUser?.displayFirstName || targetUser?.displayName || 'this person';

	// Calculate period dates and generate default title based on active review cycle or fallback to last completed quarter
	const { periodStart, periodEnd, periodDisplayText, defaultTitle } = useMemo(() => {
		// Try to use active review cycle cadence to calculate last completed period
		if (activeReviewCycle?.notificationCadence) {
			const cadence = activeReviewCycle.notificationCadence;
			const { startDate, endDate, label } = getLastCompletedPeriod(cadence);

			// Use the shared formatter for display
			const displayLabel = formatReviewCadence(cadence);

			return {
				periodStart: startDate,
				periodEnd: endDate,
				periodDisplayText: `${displayLabel} (${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')})`,
				defaultTitle: label,
			};
		}

		// Fallback to last completed quarter (not current quarter)
		const { startDate, endDate, label } = getLastCompletedPeriod('quarterly');

		return {
			periodStart: startDate,
			periodEnd: endDate,
			periodDisplayText: `Quarterly (${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')})`,
			defaultTitle: label,
		};
	}, [activeReviewCycle]);

	const {
		control,
		handleSubmit,
		watch,
		reset,
		setValue,
		formState: { errors, isValid },
	} = useForm<CreateReviewFormData>({
		defaultValues: {
			title: defaultTitle,
			timeframeType: 'current-quarter',
			timePeriodStart: periodStart,
			timePeriodEnd: periodEnd,
			isPublic: true,
			visibility: 'public',
		},
		mode: 'onChange',
	});

	const timeframeType = watch('timeframeType');
	const isPublic = watch('isPublic');
	const customStartDate = watch('timePeriodStart');
	const customEndDate = watch('timePeriodEnd');

	// Calculate date range error for custom display
	const dateRangeError = useMemo(() => {
		if (timeframeType === 'custom' && customStartDate && customEndDate) {
			const daysDiff = differenceInDays(customEndDate, customStartDate);
			if (daysDiff > 365) {
				return 'Date range cannot exceed 1 year';
			}
			if (daysDiff < 0) {
				return 'End date must be after start date';
			}
		}
		return null;
	}, [timeframeType, customStartDate, customEndDate]);

	// Helper function to generate a title from custom dates
	const generateTitleFromDates = useCallback(
		(startDate?: Date, endDate?: Date): string => {
			if (!startDate || !endDate) return defaultTitle;

			try {
				if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
					return defaultTitle;
				}

				// Simple format: "Jan - Apr 2025" or "2024-2025" for year-spanning
				if (startDate.getFullYear() === endDate.getFullYear()) {
					return `${format(startDate, 'MMM')} - ${format(endDate, 'MMM yyyy')}`;
				} else {
					return `${format(startDate, 'MMM yyyy')} - ${format(endDate, 'MMM yyyy')}`;
				}
			} catch {
				return defaultTitle;
			}
		},
		[defaultTitle]
	);

	// Update dates and title when timeframe type or default title changes
	useEffect(() => {
		if (timeframeType === 'current-quarter') {
			setValue('timePeriodStart', periodStart);
			setValue('timePeriodEnd', periodEnd);
			setValue('title', defaultTitle);
			// Reset manual edit flag when switching back to current quarter
			setIsTitleManuallyEdited(false);
		}
	}, [timeframeType, periodStart, periodEnd, defaultTitle, setValue]);

	// Auto-update title when custom dates change (if title hasn't been manually edited)
	useEffect(() => {
		if (timeframeType === 'custom' && !isTitleManuallyEdited && customStartDate && customEndDate) {
			const generatedTitle = generateTitleFromDates(customStartDate, customEndDate);
			setValue('title', generatedTitle);
		}
	}, [timeframeType, isTitleManuallyEdited, customStartDate, customEndDate, setValue, generateTitleFromDates]);

	// Update visibility when isPublic changes
	useEffect(() => {
		setValue('visibility', isPublic ? 'public' : 'private');
	}, [isPublic, setValue]);

	const handleClose = () => {
		reset({
			title: defaultTitle,
			timeframeType: 'current-quarter',
			timePeriodStart: periodStart,
			timePeriodEnd: periodEnd,
			isPublic: true,
			visibility: 'public',
		});
		setIsTitleManuallyEdited(false);
		onClose();
	};

	const onSubmit = async (data: CreateReviewFormData) => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			// Prepare the API request
			if (!data.timePeriodStart || !data.timePeriodEnd) {
				throw new Error('Time period dates are required');
			}

			// Normalize dates to noon local time and format to ISO strings
			const startDateAtNoon = new Date(data.timePeriodStart.getFullYear(), data.timePeriodStart.getMonth(), data.timePeriodStart.getDate(), 12, 0, 0, 0);
			const endDateAtNoon = new Date(data.timePeriodEnd.getFullYear(), data.timePeriodEnd.getMonth(), data.timePeriodEnd.getDate(), 12, 0, 0, 0);

			const createRequest: {
				timePeriodStart: string;
				timePeriodEnd: string;
				userId: string;
				title?: string;
				visibility: 'public' | 'private';
			} = {
				timePeriodStart: format(startDateAtNoon, 'yyyy-MM-dd'),
				timePeriodEnd: format(endDateAtNoon, 'yyyy-MM-dd'),
				userId: userId,
				title: data.title,
				visibility: data.visibility,
			};

			// Call the API
			await createReviewRecord.mutateAsync(createRequest);

			// Show success toast
			toast({
				title: "Review Recap generation has started, we'll notify you when it is ready!",
				status: 'success',
				duration: 5000,
				isClosable: true,
			});

			handleClose();
		} catch (error) {
			console.error('Failed to create review record:', error);

			// Show error toast
			toast({
				title: 'Failed to create Review Recap',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
			<ModalOverlay />
			<ModalContent borderRadius="xl" maxH="calc(100vh - 40px)" overflow="hidden">
				<ModalHeader>Create new recap</ModalHeader>
				<ModalCloseButton />

				<form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
					<ModalBody p={5} overflowY="auto" flex="1" minH={0}>
						<VStack spacing={4} align="stretch">
							{/* Recap Title */}
							<FormControl isInvalid={!!errors.title}>
								<FormLabel fontSize="sm" fontWeight="medium" color="text.softest">
									Recap title
								</FormLabel>
								<Controller
									name="title"
									control={control}
									rules={{ required: 'Recap title is required' }}
									render={({ field }) => (
										<Input
											{...field}
											size="md"
											borderRadius="md"
											onChange={(e) => {
												field.onChange(e);
												// Mark as manually edited when user types
												setIsTitleManuallyEdited(true);
											}}
										/>
									)}
								/>
								<FormErrorMessage>{errors.title?.message}</FormErrorMessage>
								<Text fontSize="xs" color="text.softest" mt={1}>
									{isTitleManuallyEdited ? 'Custom title - no longer auto-updating' : "Auto-generated based on time period. You can edit this if you'd like."}
								</Text>
							</FormControl>

							{/* Time Period */}
							<FormControl>
								<FormLabel fontSize="sm" fontWeight="medium" color="text.softest">
									Recap time period
								</FormLabel>
								<Controller
									name="timeframeType"
									control={control}
									render={({ field }) => (
										<RadioGroup {...field}>
											<Stack spacing={3}>
												{/* Current Performance Cycle - Default Option */}
												<Box
													border="1px solid"
													borderColor="border.default"
													borderRadius="md"
													p={3}
													_hover={{ borderColor: 'border.input' }}
													bg={timeframeType === 'current-quarter' ? 'primary.bgAlpha' : 'bg.surface'}
													borderWidth={timeframeType === 'current-quarter' ? '2px' : '1px'}
													borderStyle={timeframeType === 'current-quarter' ? 'solid' : 'solid'}
												>
													<Radio value="current-quarter" size="md" alignItems="flex-start">
														<VStack align="start" spacing={1}>
															<Text fontSize="md" fontWeight={timeframeType === 'current-quarter' ? 'medium' : 'normal'} mt={-0.5}>
																Current performance cycle
															</Text>
															<Text fontSize="sm" color="text.softer">
																{periodDisplayText}
															</Text>
														</VStack>
													</Radio>
												</Box>

												<Box
													border="1px solid"
													borderColor="border.default"
													borderRadius="md"
													p={3}
													_hover={{ borderColor: 'border.input' }}
													bg={timeframeType === 'custom' ? 'primary.bgAlpha' : 'bg.surface'}
													borderWidth={timeframeType === 'custom' ? '2px' : '1px'}
													borderStyle={timeframeType === 'custom' ? 'solid' : 'solid'}
													overflow="hidden"
												>
													<VStack align="start" spacing={2} w="full">
														<Radio value="custom" size="md">
															<Text fontSize="md" fontWeight={timeframeType === 'custom' ? 'medium' : 'normal'}>
																Custom timeframe
															</Text>
														</Radio>
														{timeframeType === 'custom' && (
															<VStack spacing={2} w="full" pl={7} align="stretch">
																<Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
																	<FormControl flex={1}>
																		<FormLabel fontSize="xs" color="text.softest">
																			Start date
																		</FormLabel>
																		<Controller
																			name="timePeriodStart"
																			control={control}
																			rules={{
																				required: timeframeType === 'custom' ? 'Start date is required' : false,
																			}}
																			render={({ field }) => (
																				<DatePicker
																					displayFormat="short"
																					value={field.value || null}
																					onChange={(date) => {
																						if (!date) {
																							field.onChange(null);
																							return;
																						}
																						// Normalize to noon local time to avoid timezone issues
																						const dateAtNoon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
																						field.onChange(dateAtNoon);
																					}}
																					isClearable={true}
																					w="full"
																					strategy="fixed"
																				/>
																			)}
																		/>
																	</FormControl>
																	<FormControl flex={1}>
																		<FormLabel fontSize="xs" color="text.softest">
																			End date
																		</FormLabel>
																		<Controller
																			name="timePeriodEnd"
																			control={control}
																			rules={{
																				required: timeframeType === 'custom' ? 'End date is required' : false,
																			}}
																			render={({ field }) => (
																				<DatePicker
																					displayFormat="short"
																					value={field.value || null}
																					onChange={(date) => {
																						if (!date) {
																							field.onChange(null);
																							return;
																						}
																						// Normalize to noon local time to avoid timezone issues
																						const dateAtNoon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
																						field.onChange(dateAtNoon);
																					}}
																					isClearable={true}
																					w="full"
																					strategy="fixed"
																				/>
																			)}
																		/>
																	</FormControl>
																</Stack>
																{/* Custom Date Range Error Display */}
																{dateRangeError && (
																	<Alert status="error" backgroundColor="red.50" borderRadius="md" py={2} px={3}>
																		<AlertIcon />
																		<AlertDescription display="flex" justifyContent="center" alignItems="center" color="error.text">
																			{dateRangeError}
																		</AlertDescription>
																	</Alert>
																)}
															</VStack>
														)}
													</VStack>
												</Box>
											</Stack>
										</RadioGroup>
									)}
								/>
							</FormControl>

							{/* Visibility Settings */}
							<FormControl>
								<FormLabel fontSize="sm" fontWeight="medium" color="text.softest">
									Visibility settings
								</FormLabel>
								<Controller
									name="isPublic"
									control={control}
									render={({ field: { value, onChange } }) => (
										<HStack spacing={1} align="center">
											<Checkbox isChecked={value} onChange={(e) => onChange(e.target.checked)} size="md">
												<Text fontSize="sm" mt={1}>
													{isManagerCreatingForOther ? `Share recap with ${targetDisplayName}` : 'Share recap with your manager'}
												</Text>
											</Checkbox>
											<Tooltip
												label={
													isManagerCreatingForOther
														? `${targetDisplayName} will be able to view and edith this recap`
														: 'Your manager will be able to view and edit this recap'
												}
											>
												<Box as="span" display="flex" alignItems="center" mt={1}>
													<InformationCircleOutline size="xs" color="info.icon" />
												</Box>
											</Tooltip>
										</HStack>
									)}
								/>
							</FormControl>
						</VStack>
					</ModalBody>

					<ModalFooter>
						<HStack spacing={3}>
							<Button variant="ghost" color="primary.fg" onClick={handleClose}>
								Cancel
							</Button>
							<Button
								type="submit"
								colorScheme="primary"
								isLoading={createReviewRecord.isLoading}
								loadingText="Creating..."
								isDisabled={!isValid || !!dateRangeError || createReviewRecord.isLoading}
							>
								Create recap
							</Button>
						</HStack>
					</ModalFooter>
				</form>
			</ModalContent>
		</Modal>
	);
};
